// hooks/useAudit.js
// Registra eventos de auditoria conforme LGPD Art. 46º
// Usa a função registrar_audit() do Supabase (SECURITY DEFINER)

import { useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Ações válidas — espelham o CHECK constraint do banco:
 * 'visualizou' | 'criou' | 'editou' | 'assinou' | 'exportou'
 * 'revogou_tcle' | 'solicitacao_titular' | 'login' | 'logout' | 'erro_acesso'
 */

export function useAudit() {
  /**
   * Registra um evento de auditoria.
   * Falhas são silenciosas para não bloquear o fluxo principal.
   *
   * @param {string} acao     - Uma das ações válidas acima
   * @param {string} tabela   - Nome da tabela afetada (opcional)
   * @param {string} registroId - UUID do registro afetado (opcional)
   * @param {object} detalhes - Contexto adicional em JSON (opcional)
   */
  const registrar = useCallback(async (acao, tabela = null, registroId = null, detalhes = null) => {
    try {
      const { error } = await supabase.rpc('registrar_audit', {
        p_acao: acao,
        p_tabela: tabela,
        p_registro: registroId,
        p_detalhes: detalhes ? detalhes : null,
      });

      if (error) {
        console.warn('[useAudit] Falha ao registrar evento:', error.message);
      }
    } catch (err) {
      console.warn('[useAudit] Erro inesperado:', err);
    }
  }, []);

  // ── Atalhos semânticos ────────────────────────────────────────

  /** Paciente visualizado */
  const auditVisualizou = useCallback((tabela, registroId, detalhes) =>
    registrar('visualizou', tabela, registroId, detalhes), [registrar]);

  /** Registro criado */
  const auditCriou = useCallback((tabela, registroId, detalhes) =>
    registrar('criou', tabela, registroId, detalhes), [registrar]);

  /** Registro editado */
  const auditEditou = useCallback((tabela, registroId, detalhes) =>
    registrar('editou', tabela, registroId, detalhes), [registrar]);

  /** Avaliação assinada (complementa o trigger automático do banco) */
  const auditAssinou = useCallback((registroId, detalhes) =>
    registrar('assinou', 'avaliacoes', registroId, detalhes), [registrar]);

  /** Exportação de dados (Excel, CSV, etc.) */
  const auditExportou = useCallback((detalhes) =>
    registrar('exportou', null, null, detalhes), [registrar]);

  /** TCLE revogado */
  const auditRevogouTcle = useCallback((consentimentoId, detalhes) =>
    registrar('revogou_tcle', 'consentimentos', consentimentoId, detalhes), [registrar]);

  /** Solicitação de direito do titular registrada */
  const auditSolicitacaoTitular = useCallback((solicitacaoId, detalhes) =>
    registrar('solicitacao_titular', 'solicitacoes_titular', solicitacaoId, detalhes), [registrar]);

  /** Acesso negado / erro de permissão */
  const auditErroAcesso = useCallback((detalhes) =>
    registrar('erro_acesso', null, null, detalhes), [registrar]);

  return {
    registrar,
    auditVisualizou,
    auditCriou,
    auditEditou,
    auditAssinou,
    auditExportou,
    auditRevogouTcle,
    auditSolicitacaoTitular,
    auditErroAcesso,
  };
}
